// src/pages/IssueReporting/ViewTicketsPage.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Alert,
    Grid,
    Paper
} from '@mui/material';
import CryptoJS from "crypto-js";
import { KEY, ApiDetails } from "../../dummyData";
import ErrorBoundary from '../../ErrorBoundary';

// Import custom components
import PageHeader from './components/PageHeader';
import TicketsSummary from './components/TicketSummary';
import TicketsFilters from './components/TicketFilters';
import TicketsTable from './components/TicketTable';
import TicketDetailsDialog from './components/TicketDetailsDialog';
import ImagePreviewDialog from './components/ImagePreviewDialog';

// Import mock data for fallback
import { mockTickets } from './mockData';

const ViewTicketsPage = () => {
    // State for tickets data
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // State for UI alerts
    const [alert, setAlert] = useState({show: false, message: '', severity: 'success'});

    // State for filtering and pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [priorityFilter, setPriorityFilter] = useState('All');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    // State for dialogs
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageDialogOpen, setImageDialogOpen] = useState(false);

    // State for comment functionality
    const [newComment, setNewComment] = useState('');
    const [newCommentLoading, setNewCommentLoading] = useState(false);

    // Get tenant details from localStorage
    const getTenantId = () => {
        try {
            let encryptedDetails = localStorage.getItem("tenant_details");
            if (encryptedDetails) {
                let details = CryptoJS.AES.decrypt(encryptedDetails, KEY);
                let originalText = details.toString(CryptoJS.enc.Utf8);
                if (originalText) {
                    const tenantDetails = new Map(Object.entries(JSON.parse(originalText)));
                    return tenantDetails.get("tenant_id");
                }
            }
            return null;
        } catch (error) {
            console.error("Error decrypting tenant details:", error);
            return null;
        }
    };

    // Fetch tickets from API
    useEffect(() => {
        const fetchTickets = async () => {
            try {
                setLoading(true);
                setError(null);

                const tenantId = getTenantId();

                // If no tenant ID, use mock data
                if (!tenantId) {
                    console.warn("No tenant ID found, using mock data");
                    setTickets(mockTickets);
                    setLoading(false);
                    return;
                }

                // Prepare request data for the API call
                const requestData = {
                    tenant_id: tenantId
                };

                // Make API call
                const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets/get/tenant", {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestData)
                });

                if (!response.ok) {
                    throw new Error(`API error: ${response.status}`);
                }

                const data = await response.json();

                // Process API data, ensuring all required fields are present
                const processedTickets = data.map(ticket => ({
                    id: ticket.id || `TKT-${ticket.id}`,
                    title: ticket.title || ticket.problem_name || 'Untitled Issue',
                    description: ticket.description || '',
                    category: ticket.category || ticket.problem_type_name || 'General',
                    priority: ticket.priority || 'Medium',
                    status: ticket.status || 'Open',
                    createdAt: ticket.createdAt || ticket.date_created || new Date().toISOString(),
                    updatedAt: ticket.updatedAt || ticket.date_updated || ticket.date_created || new Date().toISOString(),
                    comments: (ticket.comments || []).map(comment => ({
                        id: comment.id || `CMT-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                        text: comment.text || comment.comment || '',
                        author: comment.author || comment.author_name || 'Unknown',
                        timestamp: comment.timestamp || comment.date_created || new Date().toISOString()
                    })),
                    attachments: (ticket.attachments || []).map(attachment => ({
                        id: attachment.attachment_id || attachment.id || `ATT-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                        fileName: attachment.original_filename || attachment.file_name || 'Unknown File',
                        thumbnail: attachment.thumbnail || '',
                        fullPath: attachment.file_path || ''
                    }))
                }));

                setTickets(processedTickets);
            } catch (error) {
                console.error("Error fetching tickets:", error);
                setError("Failed to load tickets. Falling back to local data.");

                // Fallback to localStorage if API fails
                try {
                    let encryptedData = localStorage.getItem("tenant_tickets");

                    if (!encryptedData) {
                        // Initialize with mock data if not exists
                        setTickets(mockTickets);
                    } else {
                        let decrypted = CryptoJS.AES.decrypt(encryptedData, KEY);
                        let originalText = decrypted.toString(CryptoJS.enc.Utf8);

                        if (!originalText) {
                            throw new Error("Decryption resulted in empty string");
                        }

                        let parsedTickets = JSON.parse(originalText);
                        setTickets(parsedTickets || []);
                    }

                    showAlert("Using locally cached tickets due to connection issue.", "warning");
                } catch (localError) {
                    console.error("Error retrieving local tickets:", localError);
                    setTickets(mockTickets);
                    showAlert("Using demo data due to data loading errors.", "error");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchTickets();
    }, [refreshTrigger]); // Refresh when trigger changes

    // Save tickets to localStorage as backup
    const saveTickets = (updatedTickets) => {
        try {
            const encryptedData = CryptoJS.AES.encrypt(
                JSON.stringify(updatedTickets),
                KEY
            ).toString();

            localStorage.setItem("tenant_tickets", encryptedData);
            return true;
        } catch (error) {
            console.error("Error saving tickets:", error);
            showAlert("Failed to save changes. Please try again.", "error");
            return false;
        }
    };

    // Submit new comment
    const submitComment = async () => {
        if (!newComment.trim()) return;

        setNewCommentLoading(true);

        try {
            const tenantId = getTenantId();
            if (!tenantId || !selectedTicket) {
                throw new Error("Missing tenant ID or ticket information");
            }

            // Extract the numeric part of the ticket ID
            const ticketIdMatch = selectedTicket.id.match(/\d+/);
            if (!ticketIdMatch) {
                throw new Error("Invalid ticket ID format");
            }
            const ticketId = ticketIdMatch[0];

            // Prepare comment data
            const commentData = {
                issue_id: ticketId,
                tenant_id: tenantId,
                comment: newComment,
                author_type: "Tenant",
                is_internal: false
            };

            // Make API call to add comment
            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/comments/add", {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(commentData)
            });

            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }

            // Update local state
            const newCommentObj = {
                id: `CMT-${Date.now()}`,
                text: newComment,
                author: "Tenant",
                timestamp: new Date().toISOString()
            };

            const updatedTicket = {
                ...selectedTicket,
                comments: [newCommentObj, ...selectedTicket.comments],
                updatedAt: new Date().toISOString()
            };

            const updatedTickets = tickets.map(ticket =>
                ticket.id === selectedTicket.id ? updatedTicket : ticket
            );

            setSelectedTicket(updatedTicket);
            setTickets(updatedTickets);
            saveTickets(updatedTickets); // Save to localStorage as backup

            setNewComment('');
            showAlert("Comment added successfully", "success");
        } catch (error) {
            console.error("Error adding comment:", error);
            showAlert("Failed to add comment: " + error.message, "error");
        } finally {
            setNewCommentLoading(false);
        }
    };

    // Show alert message
    const showAlert = (message, severity) => {
        setAlert({
            show: true,
            message,
            severity
        });

        // Auto-hide alert after 5 seconds
        setTimeout(() => {
            setAlert(prev => ({...prev, show: false}));
        }, 5000);
    };

    // Filter tickets based on search term and filters
    const filteredTickets = tickets.filter(ticket => {
        const matchesSearch =
            (ticket.title?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (ticket.description?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (ticket.id?.toLowerCase() || '').includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'All' || ticket.status === statusFilter;
        const matchesPriority = priorityFilter === 'All' || ticket.priority === priorityFilter;

        return matchesSearch && matchesStatus && matchesPriority;
    });

    // Paginated tickets
    const paginatedTickets = filteredTickets.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Count tickets by status - FIXED to handle case-insensitive comparison and various status formats
    const calculateTicketCounts = () => {
        // Initialize counts
        const counts = {
            total: tickets.length,
            open: 0,
            inProgress: 0,
            resolved: 0,
            closed: 0
        };

        // Count tickets by normalized status
        tickets.forEach(ticket => {
            const status = (ticket.status || '').toUpperCase();

            if (status === 'OPEN') {
                counts.open++;
            } else if (status === 'IN PROGRESS' || status === 'INPROGRESS' || status === 'IN_PROGRESS') {
                counts.inProgress++;
            } else if (status === 'RESOLVED') {
                counts.resolved++;
            } else if (status === 'CLOSED') {
                counts.closed++;
            }
            // Any other statuses are just counted in the total
        });

        return counts;
    };

    // Get ticket counts
    const ticketCounts = calculateTicketCounts();

    // Event Handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const handleStatusFilterChange = (event) => {
        setStatusFilter(event.target.value);
        setPage(0);
    };

    const handlePriorityFilterChange = (event) => {
        setPriorityFilter(event.target.value);
        setPage(0);
    };

    const resetFilters = () => {
        setSearchTerm('');
        setStatusFilter('All');
        setPriorityFilter('All');
        setPage(0);
    };

    const handleRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    const openTicketDialog = (ticket) => {
        setSelectedTicket(ticket);
        setTicketDialogOpen(true);
    };

    const closeTicketDialog = () => {
        setTicketDialogOpen(false);
        setSelectedTicket(null);
        setNewComment('');
    };

    const handleNewCommentChange = (event) => {
        setNewComment(event.target.value);
    };

    const openImagePreview = (attachment) => {
        setSelectedImage(attachment);
        setImageDialogOpen(true);
    };

    const closeImagePreview = () => {
        setSelectedImage(null);
        setImageDialogOpen(false);
    };

    return (
        <ErrorBoundary>
            <div style={{ flex: 1, padding: 25, overflowY: "auto" }}>
                <Box>
                    {/* Alert for notifications */}
                    {alert.show && (
                        <Alert
                            severity={alert.severity}
                            sx={{mb: 2}}
                            onClose={() => setAlert({...alert, show: false})}
                        >
                            {alert.message}
                        </Alert>
                    )}

                    {/* Error message if present */}
                    {error && (
                        <Alert
                            severity="warning"
                            sx={{mb: 2}}
                            action={
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={handleRefresh}
                                >
                                    Retry
                                </Button>
                            }
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Page header */}
                    <PageHeader />

                    {/* Ticket summary cards */}
                    <TicketsSummary
                        ticketCounts={ticketCounts}
                    />

                    {/* Filters section */}
                    <Paper
                        elevation={0}
                        sx={{
                            mb: 3,
                            p: 2,
                            borderRadius: '10px',
                            border: '1px solid rgba(0, 0, 0, 0.08)'
                        }}
                    >
                        <TicketsFilters
                            searchTerm={searchTerm}
                            statusFilter={statusFilter}
                            priorityFilter={priorityFilter}
                            handleSearchChange={handleSearchChange}
                            handleStatusFilterChange={handleStatusFilterChange}
                            handlePriorityFilterChange={handlePriorityFilterChange}
                            resetFilters={resetFilters}
                            handleRefresh={handleRefresh}
                        />
                    </Paper>

                    {/* Tickets table */}
                    <TicketsTable
                        loading={loading}
                        tickets={tickets}
                        filteredTickets={filteredTickets}
                        paginatedTickets={paginatedTickets}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                        openTicketDialog={openTicketDialog}
                    />

                    {/* Ticket Details Dialog */}
                    {selectedTicket && (
                        <TicketDetailsDialog
                            open={ticketDialogOpen}
                            onClose={closeTicketDialog}
                            ticket={selectedTicket}
                            newComment={newComment}
                            newCommentLoading={newCommentLoading}
                            handleNewCommentChange={handleNewCommentChange}
                            submitComment={submitComment}
                            openImagePreview={openImagePreview}
                        />
                    )}

                    {/* Image Preview Dialog */}
                    {selectedImage && (
                        <ImagePreviewDialog
                            open={imageDialogOpen}
                            onClose={closeImagePreview}
                            image={selectedImage}
                            apiDetails={ApiDetails}
                        />
                    )}
                </Box>
            </div>
        </ErrorBoundary>
    );
};

export default ViewTicketsPage;